/**
 * Form primitives.
 *
 * The registration form, the volunteer profile, the attendance form and the
 * activity form are the bulk of this application. They are built from these four
 * components and nothing else, so that a required marker and an error message
 * look identical wherever staff meet them.
 *
 * **There is no `hint`.** These fields used to carry a line of grey advice under
 * the control — "Backdating is fine", "Corrections are not flagged". Removed on
 * request: twenty of them turned every form into a wall of guidance that is read
 * once and then scrolled past, and a label that needs a sentence under it is a
 * label that should be rewritten. Anything genuinely conditional belongs in the
 * error slot, which only appears when it applies.
 *
 * Accessibility is built in rather than left to each developer: every control gets
 * a real `<label for>`, `aria-invalid` when it is in error, and `aria-describedby`
 * pointing at the error message while one is showing. Getting that wrong on one
 * of twenty fields is easy; getting it wrong here is impossible.
 *
 * Section 8.40: validation here is for *UX only*. The API is the authority. A
 * field that is required must also be required in the Zod schema and, where it
 * matters, in a database constraint.
 */

import { useEffect, useId, useState } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import styles from './Field.module.css';

interface FieldShellProps {
  label: string;
  /** Shows the required marker. Does not enforce anything — the API does that. */
  required?: boolean;
  /** A server or client validation message. */
  error?: string;
  children: (ids: { controlId: string; describedBy: string | undefined }) => ReactNode;
}

function FieldShell({ label, required, error, children }: FieldShellProps) {
  const controlId = useId();
  const errorId = `${controlId}-error`;
  const describedBy = error ? errorId : undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={controlId}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>

      {children({ controlId, describedBy })}

      {error && (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

type ControlProps = 'id' | 'aria-invalid' | 'aria-describedby';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, ControlProps> {
  label: string;
  error?: string;
}

export function TextInput({ label, error, required, ...rest }: TextInputProps) {
  return (
    <FieldShell label={label} error={error} required={required}>
      {({ controlId, describedBy }) => (
        <input
          {...rest}
          id={controlId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`${styles.control} ${error ? styles.controlError : ''}`}
        />
      )}
    </FieldShell>
  );
}

/**
 * A password field with a reveal toggle.
 *
 * Typing a password you cannot see, on a shared office machine, with a
 * capitalised keyboard, is how somebody ends up locked out of an account whose
 * password they know perfectly well. The eye removes that.
 *
 * **It re-hides itself after a couple of seconds**, and the timer is the point
 * rather than a flourish. A field that stays revealed until clicked again gets
 * left revealed — the person types, submits, and walks away with their password
 * on screen behind them. Long enough to read what you typed, short enough that
 * it is gone before you have looked away.
 *
 * The toggle is a real `<button type="button">`: inside a form, a button
 * without that submits it, which here would mean the eye attempting a sign-in.
 * It is skipped in the tab order — `tabIndex={-1}` — so a keyboard user goes
 * straight from the password to the submit button, which is the path they
 * actually want; the eye solves a problem a keyboard user does not have.
 */
export interface PasswordInputProps extends Omit<TextInputProps, 'type'> {
  /** How long the password stays visible, in milliseconds. */
  revealMs?: number;
}

export const DEFAULT_REVEAL_MS = 2000;

export function PasswordInput({
  label,
  error,
  required,
  revealMs = DEFAULT_REVEAL_MS,
  ...rest
}: PasswordInputProps) {
  const [revealed, setRevealed] = useState(false);

  /*
   * Cleared on unmount, and on every re-reveal.
   *
   * Without the cleanup a component that unmounts while revealed leaves a timer
   * holding a setState — React warns, and in a form that navigates on submit
   * that is the ordinary case rather than the edge one.
   */
  useEffect(() => {
    if (!revealed) return undefined;
    const timer = setTimeout(() => setRevealed(false), revealMs);
    return () => clearTimeout(timer);
  }, [revealed, revealMs]);

  return (
    <FieldShell label={label} error={error} required={required}>
      {({ controlId, describedBy }) => (
        <div className={styles.withAffix}>
          <input
            {...rest}
            id={controlId}
            type={revealed ? 'text' : 'password'}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={`${styles.control} ${styles.controlWithAffix} ${error ? styles.controlError : ''}`}
          />
          <button
            type="button"
            tabIndex={-1}
            className={styles.affixButton}
            onClick={() => setRevealed((showing) => !showing)}
            // The name changes with the state, so a screen reader that does
            // reach it is told what pressing it will do rather than what it is.
            aria-label={revealed ? 'Hide password' : 'Show password'}
            title={revealed ? 'Hide password' : 'Show password'}
          >
            <EyeIcon crossed={revealed} />
          </button>
        </div>
      )}
    </FieldShell>
  );
}

/**
 * The eye, inline rather than from an icon set — it is the only icon in the
 * application, and a dependency for one glyph is not a trade worth making.
 * `aria-hidden`, because the button around it carries the name.
 */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d="M1.8 12S5.4 5.5 12 5.5 22.2 12 22.2 12 18.6 18.5 12 18.5 1.8 12 1.8 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.7" />
      {crossed && (
        <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      )}
    </svg>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectInputProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  ControlProps | 'children'
> {
  label: string;
  error?: string;
  options: SelectOption[];
  /** The blank first option. Omit only when a value is genuinely always present. */
  placeholder?: string;
}

export function SelectInput({
  label,
  error,
  options,
  placeholder = 'Select…',
  required,
  ...rest
}: SelectInputProps) {
  return (
    <FieldShell label={label} error={error} required={required}>
      {({ controlId, describedBy }) => (
        <select
          {...rest}
          id={controlId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`${styles.control} ${error ? styles.controlError : ''}`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

export interface CheckboxGroupProps {
  label: string;
  error?: string;
  required?: boolean;
  options: SelectOption[];
  /** The selected codes. Controlled — the page owns the array. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /**
   * How the choices are arranged. `columns` — the default — lays them out in a
   * grid sized so terms of up to ~23 characters sit on one line and line up
   * across rows. `stack` puts one per row, for options that are sentences and
   * would otherwise break or force a single very wide column.
   */
  layout?: 'columns' | 'stack';
}

/**
 * A multi-select over a `reference_values` category — areas of interest,
 * languages.
 *
 * Checkboxes rather than a `<select multiple>`: the native multi-select needs
 * ctrl-click to pick a second option, which a first-time volunteer filling in a
 * public form on a phone will not discover.
 *
 * It renders a `<fieldset>` with a `<legend>`, so the group has one accessible
 * name and the error is announced against the group rather than against whichever
 * checkbox happens to be last. `FieldShell` is not reused here for exactly that
 * reason — its `<label for>` addresses a single control.
 */
export function CheckboxGroup({
  label,
  error,
  required,
  options,
  value,
  onChange,
  disabled,
  layout = 'columns',
}: CheckboxGroupProps) {
  const groupId = useId();

  const errorId = `${groupId}-error`;
  const describedBy = error ? errorId : undefined;

  const toggle = (code: string) => {
    onChange(value.includes(code) ? value.filter((item) => item !== code) : [...value, code]);
  };

  return (
    <fieldset className={styles.group} aria-describedby={describedBy}>
      <legend className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </legend>

      <div className={layout === 'stack' ? styles.optionsStack : styles.options}>
        {options.map((option) => (
          <label key={option.value} className={styles.checkbox}>
            <input
              type="checkbox"
              checked={value.includes(option.value)}
              onChange={() => toggle(option.value)}
              disabled={disabled}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {error && (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export interface RadioGroupProps {
  label: string;
  error?: string;
  required?: boolean;
  options: SelectOption[];
  /** The selected code, or '' for none. Controlled — the page owns the value. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * How the choices are arranged. `columns` — the default — lays them out in a
   * grid sized so terms of up to ~23 characters sit on one line and line up
   * across rows. `stack` puts one per row, for options that are sentences and
   * would otherwise break or force a single very wide column.
   */
  layout?: 'columns' | 'stack';
}

/**
 * A single-choice group — "how are you volunteering?", the kind of question a
 * `<select>` hides one option of behind a click. Radios show every choice at
 * once, which is what a first-time visitor on a public form needs.
 *
 * The `<fieldset>`/`<legend>` shape mirrors `CheckboxGroup` for the same
 * accessibility reason: the group has one name and the error is announced against
 * the group, not against whichever radio happens to be last.
 */
export function RadioGroup({
  label,
  error,
  required,
  options,
  value,
  onChange,
  disabled,
  layout = 'columns',
}: RadioGroupProps) {
  const groupId = useId();

  const errorId = `${groupId}-error`;
  const describedBy = error ? errorId : undefined;

  return (
    <fieldset className={styles.group} aria-describedby={describedBy}>
      <legend className={styles.label}>
        {label}
        {required && (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </legend>

      <div className={layout === 'stack' ? styles.optionsStack : styles.options}>
        {options.map((option) => (
          <label key={option.value} className={styles.checkbox}>
            <input
              type="radio"
              name={groupId}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              disabled={disabled}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>

      {error && (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export interface TextAreaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  ControlProps
> {
  label: string;
  error?: string;
}

export function TextArea({ label, error, required, rows = 4, ...rest }: TextAreaProps) {
  return (
    <FieldShell label={label} error={error} required={required}>
      {({ controlId, describedBy }) => (
        <textarea
          {...rest}
          id={controlId}
          rows={rows}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`${styles.control} ${error ? styles.controlError : ''}`}
        />
      )}
    </FieldShell>
  );
}
